/**
 * The list of sources Sam actually checks for one location, honestly composed from real,
 * already-fetched facts. Never invents a source, and never implies a feed is being read when it
 * is not configured — the current-events feed ships disabled (zero approved sources today), so
 * that section normally renders as "none configured", which is the correct, honest state.
 *
 * A feed-derived title is untrusted third-party text: this module returns it tagged
 * `kind: 'context_feed'` so a renderer can display it as quoted, attributed source content and
 * never interpolate it into the product's own sentences.
 */

import type { SourceHealthLike } from '../types';

export type { SourceHealthLike };

export type SourceCheckedKind = 'calendar' | 'business_source' | 'context_feed';

export interface ContextSourceLike {
  key: string;
  label: string;
  kind: 'calendar' | 'context_feed';
}

export interface SourceCheckedRow {
  key: string;
  label: string;
  kind: SourceCheckedKind;
  /** `always_available` is the calendar's own real state (a local library, never fetched, never
   *  gated). `not_checked` is a context feed that is configured but has no fetch result plumbed
   *  through to this composer yet — deliberately NOT `current`. Defaulting an un-fetched feed to
   *  `current` would render as "Up to date", a positive freshness claim with no backing check. */
  state: SourceHealthLike['state'] | 'always_available' | 'not_checked';
  reasonCode: string | null;
}

const BUSINESS_SOURCE_LABELS: Record<string, string> = {
  reviews: 'Customer reviews',
  gbp_profile: 'Business profile details',
  search_terms: 'Search performance',
  site_audit: 'Website health',
};

export function businessSourceLabel(sourceKey: string): string {
  return BUSINESS_SOURCE_LABELS[sourceKey] ?? sourceKey;
}

/**
 * Combine business-source health (from the backend's `sourceHealth`, already computed and never
 * re-derived here) with the calendar/context-feed list (from agent-suite's mission snapshot) into
 * one honest "sources Sam checks" list.
 */
export function composeSourcesChecked(
  sourceHealth: readonly SourceHealthLike[],
  contextSources: readonly ContextSourceLike[],
): SourceCheckedRow[] {
  const businessRows: SourceCheckedRow[] = sourceHealth.map((health) => ({
    key: health.sourceKey,
    label: businessSourceLabel(health.sourceKey),
    kind: 'business_source',
    state: health.state,
    reasonCode: health.reasonCode,
  }));

  const contextRows: SourceCheckedRow[] = contextSources.map((source) => ({
    key: source.key,
    label: source.label,
    kind: source.kind,
    // The calendar is a local library, never fetched over the network and never gated by
    // configuration — it has no meaningful "not connected"/"delayed" state of its own, so
    // `always_available` is a real fact, not a guess. A context feed is the opposite: this
    // composer has no fetch result for it at all (that would have to come from a real per-feed
    // check the product does not perform yet), so it is `not_checked` — never defaulted to
    // `current`, which would falsely claim "Up to date".
    state: source.kind === 'calendar' ? 'always_available' : 'not_checked',
    reasonCode: null,
  }));

  return [...contextRows, ...businessRows];
}

export function hasNoConfiguredContextFeeds(contextSources: readonly ContextSourceLike[]): boolean {
  return !contextSources.some((source) => source.kind === 'context_feed');
}

const SOURCE_CHECKED_STATE_TEXT: Record<SourceCheckedRow['state'], string> = {
  always_available: 'Always available',
  not_checked: 'Not checked yet',
  not_connected: 'Not connected',
  never_checked: 'Not checked yet',
  current: 'Up to date',
  delayed: 'Check is delayed',
  error: 'Check failed',
};

/** The one place a row's state becomes display text — so a caller cannot bypass the
 *  `not_checked` guard above by writing its own fallback that defaults to "Up to date". */
export function sourceCheckedStateText(state: SourceCheckedRow['state']): string {
  return SOURCE_CHECKED_STATE_TEXT[state];
}
